// Components/Layout/Pages/EventAdmin/EventAdminLogin.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getData, postData } from '../../../../services/apiService';
import Swal from 'sweetalert2';

const EventAdminLogin = () => {
    const { eventId } = useParams();
    const [eventName, setEventName] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();

    const [loginForm, setLoginForm] = useState({
        mobile: '',
        password: ''
    });

    // Check for existing session on component mount
    useEffect(() => {
        const checkExistingSession = () => {
            const sessionData = localStorage.getItem(`event_admin_${eventId}`);
            if (sessionData) {
                const { mobile, timestamp } = JSON.parse(sessionData);
                const currentTime = Date.now();
                const sessionAge = currentTime - timestamp;
                const FIVE_MINUTES = 5 * 60 * 1000; // 5 minutes in milliseconds

                // If session is less than 5 minutes old, auto-fill mobile and show message
                if (sessionAge < FIVE_MINUTES) {
                    setLoginForm(prev => ({ ...prev, mobile }));

                    // Show info message about existing session
                    Swal.fire({
                        title: 'Session Detected',
                        text: 'You have an existing session. Please continue with OTP verification.',
                        icon: 'info',
                        timer: 3000,
                        showConfirmButton: false,
                        position: 'top-end',
                        toast: true
                    });
                } else {
                    // Clear expired session
                    localStorage.removeItem(`event_admin_${eventId}`);
                    localStorage.removeItem(`event_admin_mobile_${eventId}`);
                }
            }
        };

        if (eventId) {
            checkExistingSession();
        }
    }, [eventId]);

    const handleLoginChange = (e) => {
        setLoginForm({
            ...loginForm,
            [e.target.name]: e.target.value
        });
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    useEffect(() => {
        const fetchEventName = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await getData(`Event/GetEventName?eventid=${eventId}`);

                if (!response) {
                    throw new Error('No response from server');
                }

                if (response.isError === false && response.data && response.data.length > 0) {
                    setEventName(response.data[0].Event_Name);
                } else {
                    const errorMessage = response.message ||
                        response.Result?.message ||
                        'Failed to fetch event details';
                    throw new Error(errorMessage);
                }
            } catch (err) {
                console.error('Error fetching event name:', err);
                setError(err.message);
                Swal.fire({
                    title: 'Error!',
                    text: err.message || 'Failed to load event information',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
            } finally {
                setLoading(false);
            }
        };

        if (eventId) {
            fetchEventName();
        } else {
            setError('Event ID is required');
            setLoading(false);
        }
    }, [eventId]);

    const handleLogin = async (e) => {
        e.preventDefault();

        const mobile = loginForm.mobile?.trim();
        const password = loginForm.password?.trim();

        /* ------------------ Basic Validation ------------------ */
        if (!mobile) {
            Swal.fire({
                icon: 'error',
                title: 'Missing Details',
                text: 'Please enter both mobile number and password',
            });
            return;
        }

        const mobileRegex = /^[0-9]{10}$/;
        if (!mobileRegex.test(mobile)) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid Mobile Number',
                text: 'Please enter a valid 10-digit mobile number',
            });
            return;
        }

        try {
            setSubmitting(true);

            const requestBody = {
                Event_ID: eventId,
                Mobile_No: mobile,
                Password: mobile,
            };

            const response = await postData('Event/send-admin-mobile-otp', requestBody);

            if (!response) {
                throw new Error('Server did not respond. Please try again.');
            }

            if (response.isError) {
                throw new Error(response.message || 'Failed to send OTP');
            }

            /* ------------------ Success Flow ------------------ */

            // Save required data
            localStorage.setItem(`event_admin_mobile_${eventId}`, mobile);

            // Show success toast
            Swal.fire({
                icon: 'success',
                title: 'OTP Sent',
                text: 'A 6-digit OTP has been sent to your registered mobile number',
                toast: true,
                position: 'top-end',
                timer: 2500,
                showConfirmButton: false,
            });

            // Redirect after slight delay for better UX
            setTimeout(() => {
                navigate(`/admin/event/${eventId}/otp`, {
                    replace: true,
                });
            }, 800);

        } catch (error) {
            console.error('OTP Send Error:', error);

            // Clear session on failure
            localStorage.removeItem(`event_admin_mobile_${eventId}`);

            Swal.fire({
                icon: 'error',
                title: 'Login Failed',
                text: error.message || 'Unable to send OTP. Please try again.',
            });
        } finally {
            setSubmitting(false);
        }
    };


    // Show loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
                <div className="text-center">
                    <div className="w-20 h-20 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading Event Information</h2>
                    <p className="text-gray-600">Please wait...</p>
                </div>
            </div>
        );
    }

    // Show error state
    if (error && !eventName) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
                <div className="text-center max-w-md">
                    <div className="w-20 h-20 bg-gradient-to-r from-red-100 to-red-200 rounded-full flex items-center justify-center mx-auto mb-6">
                        <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-3">Event Not Found</h1>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <div className="space-y-4">
                        <button
                            onClick={() => window.history.back()}
                            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl font-medium"
                        >
                            Go Back
                        </button>
                        <button
                            onClick={() => window.location.reload()}
                            className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white px-8 py-3 rounded-xl hover:from-gray-700 hover:to-gray-800 transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl font-medium"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-2xl border border-white/20">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        </div>
                        <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                            Admin Access
                        </h2>
                        <p className="text-gray-600 font-medium text-lg mb-1">
                            {eventName}
                        </p>
                    </div>

                    <form className="space-y-6" onSubmit={handleLogin}>
                        <div className="space-y-5">
                            <div>
                                <label htmlFor="mobile" className="block text-sm font-semibold text-gray-700 mb-2">
                                    Mobile Number
                                </label>
                                <div className="relative">
                                    <input
                                        id="mobile"
                                        name="mobile"
                                        type="tel"
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white"
                                        placeholder="Enter 10-digit mobile number"
                                        value={loginForm.mobile}
                                        onChange={handleLoginChange}
                                        onKeyDown={(e) => {
                                            const allowedKeys = [
                                                "Backspace",
                                                "Delete",
                                                "ArrowLeft",
                                                "ArrowRight",
                                                "Tab",
                                            ];

                                            if (allowedKeys.includes(e.key)) return;

                                            if (!/^[0-9]$/.test(e.key)) {
                                                e.preventDefault();
                                                return;
                                            }

                                            if (e.target.value.length >= 10) {
                                                e.preventDefault();
                                            }
                                        }}
                                        maxLength={10}
                                        disabled={submitting}
                                    />

                                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    Enter the mobile number registered for admin access
                                </p>
                            </div>
                        </div>

                        <div className="pt-4">
                            <button
                                type="submit"
                                disabled={!loginForm.mobile || submitting}
                                className={`w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold transition-all duration-300 transform shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${!submitting ? 'hover:from-blue-700 hover:to-purple-700 hover:-translate-y-0.5 hover:shadow-xl' : ''
                                    } ${(!loginForm.mobile || submitting) ? 'opacity-50 cursor-not-allowed transform-none' : ''}`}
                            >
                                {submitting ? (
                                    <div className="flex items-center justify-center">
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                        Sending OTP...
                                    </div>
                                ) : (
                                    'Continue to OTP Verification'
                                )}
                            </button>
                        </div>

                        <div className="text-center pt-4 border-t border-gray-200">
                            <p className="text-sm text-gray-600">
                                Need help? Contact event support
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EventAdminLogin;